import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  AttachFile as AttachIcon,
} from '@mui/icons-material';
import { invoicesApi } from '../../services/api';
import { format } from 'date-fns';
import { formatNuf } from '../../utils/formatNuf';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const EmailPreviewDialog = ({ open, onClose, invoiceId, onSend }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && invoiceId) {
      loadPreview();
    } else {
      setPreview(null);
      setError(null);
    }
  }, [open, invoiceId]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await invoicesApi.getEmailPreview(invoiceId);
      setPreview(response.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || 'Errore nel caricamento dell\'anteprima');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!preview?.can_send) return;
    
    setSending(true);
    try {
      if (onSend) {
        await onSend(invoiceId);
      } else {
        await invoicesApi.sendEmail(invoiceId);
      }
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Errore durante l\'invio');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={sending ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Anteprima Email</Typography>
            {preview?.nuf && (
              <Chip label={`Fattura ${formatNuf(preview.sezionale, preview.nuf)}`} size="small" color="primary" variant="outlined" />
            )}
          </Box>
          <IconButton onClick={onClose} size="small" disabled={sending}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && preview && (
          <Box>
            {/* Avviso se non inviabile */}
            {!preview.can_send && preview.exclusion_reason && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {preview.exclusion_reason}
              </Alert>
            )}

            {/* Intestazione email */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60 }}>
                    A:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {preview.customer_name}
                    </Typography>
                    {preview.to_email && (
                      <Chip 
                        label={preview.to_email} 
                        size="small" 
                        color={preview.can_send ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60 }}>
                    Oggetto:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {preview.subject}
                  </Typography>
                </Box>
                
                <Divider />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 60 }}>
                    Allegato:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      fattura_{formatNuf(preview.sezionale, preview.nuf)}.pdf
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Corpo email */}
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Corpo del messaggio
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'background.paper',
                minHeight: 200,
                '& p': { margin: '0 0 0.5em 0' },
                '& strong': { fontWeight: 'bold' },
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: preview.body_html }} 
                style={{ fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.6 }}
              />
            </Paper>

            {/* Info aggiuntive */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                📧 L'email includerà in allegato il PDF della fattura con bollettino
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          Chiudi
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading || !preview?.can_send || sending}
        >
          {sending ? 'Invio...' : 'Invia Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewDialog;
